import React, { useEffect, useState } from 'react'
import { LuCheck } from 'react-icons/lu'

import Select from '@/components/select/Select'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useReferralStatusesOptions } from '@/hooks/useReferralStatusesOptions'
import {
  BulkUpdatePayload,
  ReferralsImportItem,
  ReferralsImportItemPartial,
} from '@/redux/slices/referralsImportBase'
import { SelectOption } from '@/types/misc'

interface Props {
  importItem: ReferralsImportItem
  updateImportItem: (item: ReferralsImportItemPartial) => void
  bulkUpdateItems: (payload: BulkUpdatePayload) => void
  bulkUpdateSelectedOnly: boolean
}

const StatusCell: React.FC<Props> = ({
  importItem,
  updateImportItem,
  bulkUpdateItems,
  bulkUpdateSelectedOnly,
}) => {
  const {
    options: statusOptions,
    statuses,
    statusesMap,
    isLoading,
  } = useReferralStatusesOptions(
    importItem.isToInvite ? null : importItem.toCompany?.id || null,
    true,
  )
  const [isEditing, setIsEditing] = useState(false)
  const isStatusSet = !!importItem.status

  useEffect(() => {
    // track options list change (when company changes) and find similar status based on name
    if (importItem.status?.['@id'] && importItem.status?.name && !isLoading) {
      const status = statuses.find((s) => s.name === importItem.status?.name)
      if (status?.['@id'] != importItem.status?.['@id']) {
        updateImportItem({
          refUUID: importItem.refUUID,
          status: status,
          statusDefaultUsed: false,
        })
      }
    }
  }, [
    importItem.refUUID,
    importItem.status,
    updateImportItem,
    statuses,
    isLoading,
  ])

  function handleChange(selectedOption: SelectOption | null) {
    updateImportItem({
      refUUID: importItem.refUUID,
      status:
        selectedOption && statusesMap.has(selectedOption.value)
          ? statusesMap.get(selectedOption.value)
          : null,
      statusDefaultUsed: false,
    })

    setIsEditing(false)
  }
  function handleBulkApply() {
    bulkUpdateItems({
      filter: { selected: bulkUpdateSelectedOnly },
      data: { status: importItem.status },
    })
  }

  const bulkUpdateScope = !bulkUpdateSelectedOnly
    ? 'all'
    : importItem.selected
      ? 'selected'
      : 'none'

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : !isEditing && isStatusSet ? (
        <>
          <span className="cursor-pointer" onClick={() => setIsEditing(true)}>
            {
              statusOptions.find(
                (option) => option.value === importItem.status?.['@id'],
              )?.label
            }
          </span>
          {importItem.statusDefaultUsed && (
            <>
              <span className="text-warning"> (default)</span>
            </>
          )}
          <span className="show-on-row-hover text-xs">
            {', '}
            <Button variant="plain" onClick={() => setIsEditing(true)}>
              change
            </Button>

            {bulkUpdateScope == 'all' && (
              <>
                {' or '}
                <Button variant="plain" onClick={handleBulkApply}>
                  apply&nbsp;to&nbsp;all
                </Button>
              </>
            )}

            {bulkUpdateScope == 'selected' && (
              <>
                {' or '}
                <Button variant="plain" onClick={handleBulkApply}>
                  apply&nbsp;to&nbsp;selected
                </Button>
              </>
            )}
          </span>
        </>
      ) : (
        <>
          <Select
            placeholder="Select status..."
            options={statusOptions}
            value={statusOptions.find(
              (option) => option.value === importItem.status?.['@id'],
            )}
            onChange={handleChange}
            onBlur={() => setIsEditing(false)}
            className=""
          />

          <span className="mt-1 block text-right">
            <Button
              size="icon-xs"
              variant="outline-primary"
              onClick={() => setIsEditing(false)}
            >
              <LuCheck />
            </Button>
          </span>
        </>
      )}
    </>
  )
}

export default StatusCell
